import { useContext, useMemo } from "react";

import { TenantContext } from "../../../../templates/context/TenantContext";

import { Wrapper, UpperCardsRow, LowerCardsRow } from "../shared.home.style";

import { i18n } from "../../../../translate/i18n";

import { Default } from "../../../../components/Default/Default";

import { PageHeading } from "../../../../components/StyledComponents/styleComponents.index";

import { AssortmentCard, UpperCardModel } from "../components/home.index";

import {
  buildOpportunitiesCardDataChannelInfo,
  buildOpportunitiesCardDataProductInfo,
  buildProductCardData,
  buildChannelCardData,
} from "./home.data";

import { LoadingContent } from "../../../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../../../components/SharedComponents/LoadingContent/loading.content.constants";

import { useOtbViewModel } from "../../../../viewModel/useOtbVewModel";

import { createIntFormatter, createPercentFormatter, currencyFormatter } from "../../../../utils/formatter/formatter";
import { useAnalysisViewModel } from "../../../useAnalysysViewModel";

export function Home() {

  const { 
    unit,
    tenant,
    hasChannelOpportunities,
    hasProductOpportunities,
    hasPlanvsforecastAnalysis,
    hasForecastAssertivity
  } = useContext(TenantContext);

  const otbOptions = useMemo(
    () => ({ shouldFecthOppCounter: true, shouldFetchInventory: true }),
    []
  );
  const vmOtb = useOtbViewModel(otbOptions);

  const vmAnalysis = useAnalysisViewModel({ 
    shouldFetchConsolidatedPredictions: hasProductOpportunities
  })

  const { t } = i18n;

  const calculatePlanVsForecast = useMemo(() => {
    const { monthsCount, sumClientPrediction, sumAidronPrediction } = vmAnalysis?.ctx?.consolidatedData?.reduce(
      (acc, month) => {
        if (month.clientPrediction && month.aidronPrediction) {
          acc.monthsCount += 1;
          acc.sumClientPrediction += month.clientPrediction;
          acc.sumAidronPrediction += month.aidronPrediction;
        }
        return acc;
      },
      {
        monthsCount: 0,
        sumClientPrediction: 0,
        sumAidronPrediction: 0,
      }
    ) ?? {
      monthsCount: 0,
      sumClientPrediction: 0,
      sumAidronPrediction: 0,
    };

    return {
      monthsCount,
      value: sumClientPrediction - sumAidronPrediction,
      percentDifference:
        sumAidronPrediction !== 0
          ? ((sumClientPrediction - sumAidronPrediction) / sumAidronPrediction) * 100
          : 0,
    };
  }, [vmAnalysis?.ctx?.consolidatedData]);

  const { value: planVsForecastValue, percentDifference: rawPercentDifference } = calculatePlanVsForecast || {}; 

  const planVsForecast = useMemo(
    () => createIntFormatter().format(Math.abs(planVsForecastValue)),
    [planVsForecastValue]
  );

  const percentDifference = useMemo(
    () => createPercentFormatter({ 
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(rawPercentDifference / 100),
    [rawPercentDifference]
  );

  const difference = useMemo(
    () => !!rawPercentDifference ? { arrow: "↑", word: t("above") } : { arrow: "↓", word: t("below") },
    [rawPercentDifference]
  );

  const waitingData = true;

  const opportunitiesCardDataProductInfo = useMemo(
    () => buildOpportunitiesCardDataProductInfo({ t, opportunitiesTotal: vmOtb?.ctx?.opportunitiesCount?.totalValue }),
    [vmOtb?.ctx?.opportunitiesCount?.totalValue]
  );

  const opportunitiesCardDataChannelInfo = useMemo(
    () => buildOpportunitiesCardDataChannelInfo({ t, opportunitiesTotal: vmOtb?.ctx?.opportunitiesCount?.totalValue, waitingData }),
    [vmOtb?.ctx?.opportunitiesCount?.totalValue]
  );

  const productCardData = useMemo(
    () => buildProductCardData({ t, unit, planVsForecast, percentDifference, difference }),
    [unit, planVsForecast, percentDifference, difference]
  );

  const channelCardData = useMemo(
    () => buildChannelCardData({ t, opportunitiesTotal: hasChannelOpportunities, waitingData }),
    [hasChannelOpportunities]
  );

  const upperCardOpportunitiesCtx = useMemo(() => ({
    title: t("opportunitiesAnalysis"),
    text: t("stockOrSalesIncreaseOptimization"),
    leftCard: opportunitiesCardDataProductInfo,
    rightCard: opportunitiesCardDataChannelInfo
  }), [opportunitiesCardDataProductInfo, opportunitiesCardDataChannelInfo]);

  const upperCardPlanCtx = useMemo(() => ({
    title: t("planAdjustment"),
    text: t('difSumMonths'),
    leftCard: productCardData,
    rightCard: channelCardData,
  }), [productCardData, channelCardData]);

  const assortmentCtx = useMemo(() => ({ number: "23", variant: "assortment" }), []);

  const purchaseAdjustmentCtx = useMemo(() => ({
    number: vmOtb?.ctx?.purchaseAdjustmentData || {},
    variant: "purchaseAdjustment"
  }), [vmOtb?.ctx?.purchaseAdjustmentData]);

  const channelCtx = useMemo(() => ({
    number: currencyFormatter.format(vmOtb?.ctx?.lostSalesRiskSum || 0),
    variant: "channel"
  }), [vmOtb?.ctx?.lostSalesRiskSum]);
  
  if(vmAnalysis?.uiState?.isLoadingConsolidatedPredictions || vmOtb?.uiState.isLoadingOppCount) {
    return (
      <LoadingContent label={"Carregando seus dados da home..."} type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE}/>
    )
  };

  return (
    <Default>
      <Wrapper>
        <PageHeading 
          size="lg" 
          marginBottom={'0.5rem'}>
          {tenant}
        </PageHeading>
        
        <UpperCardsRow>
          <UpperCardModel ctx={upperCardOpportunitiesCtx}/>
          <UpperCardModel ctx={upperCardPlanCtx} uiState={{ adjustmentPlan: true }}/>
        </UpperCardsRow>

        <LowerCardsRow>
          <AssortmentCard ctx={assortmentCtx} />
          <AssortmentCard ctx={purchaseAdjustmentCtx} />
          <AssortmentCard ctx={channelCtx} />
        </LowerCardsRow>
      </Wrapper>
    </Default>
  );
}